/* ============================================================
 * BYOP Cart Bundle Guard
 *
 * Watches the cart for incomplete BYO bundles. A "bundle" = all cart
 * lines sharing the same `_bundle_id` property (set when the customer
 * adds a Build-Your-Own mix from the product page). Each bundle must
 * sum to a multiple of 7 spools to be valid.
 *
 * When invalid, this script:
 *   1. Renders a warning banner inside [data-byop-cart-warning] slots
 *      (one in the cart drawer footer, one above the cart-page checkout).
 *   2. Disables the checkout button so the customer can't proceed.
 *
 * It does NOT auto-remove items — the customer decides whether to add
 * more spools (from the product page) or remove the bundle.
 *
 * This is client-side prevention only. The cart-validation Shopify
 * Function (when deployed) provides server-side enforcement.
 * ============================================================ */
(function () {
  'use strict';

  const SEVEN = 7;
  const WARN_SLOT_SELECTOR = '[data-byop-cart-warning]';
  const CHECKOUT_BTN_SELECTOR = 'button[name="checkout"], input[name="checkout"]';
  const BLOCKED_ATTR = 'data-byop-checkout-blocked';

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .catch(() => null);
  }

  // Treats every mix_match line in the cart as one logical bundle —
  // customer sees one number ("total BYO spools"), one deficit, one
  // banner. Internal bundle_id fragmentation (which can happen if a
  // customer accumulated multiple bundles before we converged to one)
  // is hidden from the UI; only the aggregate matters for validity.
  function findIncompleteBundles(cart) {
    if (!cart || !cart.items) return [];
    let total = 0;
    for (const item of cart.items) {
      const props = item.properties || {};
      if (props._purchase_mode !== 'mix_match') continue;
      total += item.quantity;
    }
    if (total === 0 || total % SEVEN === 0) return [];
    const need = SEVEN - (total % SEVEN);
    return [{ total: total, need: need, target: total + need }];
  }

  function bannerHTML(issue) {
    const more = issue.need === 1 ? '1 more spool' : issue.need + ' more spools';
    return (
      '<div role="status" style="margin:14px 0;padding:13px 16px;background:#fff3df;border:1px solid #f5d8a2;border-radius:8px;font-size:13px;line-height:1.5;color:#8a4a00;">' +
        '<strong style="display:block;margin-bottom:2px;font-size:13px;">Your bundle isn\'t complete</strong>' +
        'You\'ve added ' + issue.total + ' spools — add ' + more + ' to reach ' + issue.target +
        ' for the pack discount, or remove the bundle to check out.' +
      '</div>'
    );
  }

  function render(issues) {
    const slots = document.querySelectorAll(WARN_SLOT_SELECTOR);
    const html = issues.length
      ? issues.map(bannerHTML).join('')
      : '';
    slots.forEach(slot => { slot.innerHTML = html; });
    setCheckoutBlocked(issues.length > 0);
  }

  function setCheckoutBlocked(blocked) {
    document.querySelectorAll(CHECKOUT_BTN_SELECTOR).forEach(btn => {
      // Only manage state we own — don't fight the theme's own disabled
      // attribute (e.g. terms-checkbox disabled state).
      if (blocked) {
        // Stash whether it was already disabled before we touched it so we
        // can restore correctly later.
        if (!btn.hasAttribute(BLOCKED_ATTR)) {
          btn.setAttribute(BLOCKED_ATTR, btn.disabled ? 'pre-disabled' : 'enabled');
        }
        btn.disabled = true;
        btn.style.opacity = '0.4';
        btn.style.cursor = 'not-allowed';
      } else {
        if (btn.hasAttribute(BLOCKED_ATTR)) {
          const wasPreDisabled = btn.getAttribute(BLOCKED_ATTR) === 'pre-disabled';
          btn.disabled = wasPreDisabled;
          btn.style.opacity = '';
          btn.style.cursor = '';
          btn.removeAttribute(BLOCKED_ATTR);
        }
      }
    });
  }

  let pending = false;
  function validate() {
    if (pending) return;
    pending = true;
    fetchCart().then(cart => {
      pending = false;
      render(findIncompleteBundles(cart));
    });
  }

  function init() {
    // Re-validate whenever the theme says the cart changed.
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, validate);
    }
    document.addEventListener('cart:refresh', validate);
    // Initial state on page load + a small delay to catch async theme renders.
    validate();
    setTimeout(validate, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
